"use client";

import { MapPinIcon } from "@heroicons/react/24/outline";
import { searchLocations } from "api/locations/locations";
import { useData } from "data/data-provider";
import { FC, useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import ClearDataButton from "./ClearDataButton";
import LocationDropoffModal from "./LocationDropoffModal";

export interface LocationInputProps {
  placeHolder?: string;
  desc?: string;
  className?: string;
  divHideVerticalLineClass?: string;
  autoFocus?: boolean;
  valueFromParent?: string;
  searchBar?: boolean;
}

const DropOffLocationInput: FC<LocationInputProps> = ({
  autoFocus = false,
  placeHolder = "Location",
  desc = "Where are you going?",
  className = "nc-flex-1.5",
  divHideVerticalLineClass = "left-10 -right-0.5",
  searchBar,
}) => {
  const {
    ourLocation,
    setDropoffLocation,
    searchedDropoffLocation,
    setSearchedDropoffLocation,
    dropOffLocation,
    ourCompany,
  } = useData();
  const [searchedLocations, setSearchedLocations] = useState<any>();
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [showLocationModal, setShowLocationModal] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState<any>(null);
  const [showPopover, setShowPopover] = useState(autoFocus);

  const callSearchLocationsAPI = async (input: string) => {
    try {
      const data = (await searchLocations(input)) as any;
      setSearchedLocations(data.predictions);
    } catch (error: any) {
      toast.error(error.message); // Or handle errors in a more sophisticated way
    }
  };

  useEffect(() => {
    callSearchLocationsAPI(searchedDropoffLocation);
  }, [searchedDropoffLocation]);

  useEffect(() => {
    setShowPopover(autoFocus);
  }, [autoFocus]);

  useEffect(() => {
    if (eventClickOutsideDiv) {
      document.removeEventListener("click", eventClickOutsideDiv);
    }
    showPopover && document.addEventListener("click", eventClickOutsideDiv);
    return () => {
      document.removeEventListener("click", eventClickOutsideDiv);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showPopover]);

  useEffect(() => {
    if (showPopover && inputRef.current) {
      inputRef.current.focus();
    }
  }, [showPopover]);

  const eventClickOutsideDiv = (event: MouseEvent) => {
    if (!containerRef.current) return;
    // CLICK IN_SIDE
    if (!showPopover || containerRef.current.contains(event.target as Node)) {
      return;
    }
    // CLICK OUT_SIDE
    setShowPopover(false);
  };

  useEffect(() => {
    if (dropOffLocation === null) {
      setSearchedDropoffLocation(""); // Clear state
      if (inputRef.current) {
        inputRef.current.value = ""; // Clear the input field display
      }
    }
  }, [dropOffLocation, setSearchedDropoffLocation]);

  const handleSelectLocation = (item: any) => {
    setSearchedDropoffLocation("");
    setDropoffLocation(null);

    if (item.radius) {
      setSelectedLocation(item);
      setShowLocationModal(true);
      setShowPopover(false);
    } else {
      setDropoffLocation(item);
      setShowPopover(false);
    }
  };

  const renderRecentSearches = () => {
    return (
      <>
        <div className="rounded-lg border border-[#EAECF0] bg-white shadow-lg p-0">
          {ourLocation
            .filter((item) => item.active !== false)
            .sort((a, b) => a.price - b.price)
            .map((item: any) => (
              <div
                key={item.id}
                onClick={() => handleSelectLocation(item)}
                className="flex items-center justify-between px-4 sm:px-6 py-4 cursor-pointer hover:bg-[#F4F6FB] rounded-lg transition text-text-color"
                style={{ fontWeight: 500, fontSize: "17px" }}
              >
                <div className="flex items-center flex-1 min-w-0">
                  <MapPinIcon className="w-5 h-5 text-text-color flex-shrink-0" />
                  <span className="truncate">{item.name}</span>
                </div>
                <span
                  className="font-bold text-right text-text-color flex-shrink-0 sm:ml-4 ml-0"
                  style={{ minWidth: 90 }}
                >
                  {item.price === 0 ? "Free" : "$" + item.price + "/trip"}
                </span>
              </div>
            ))}
        </div>
      </>
    );
  };

  const handleClear = () => {
    setSearchedDropoffLocation("");
    setDropoffLocation(null);
    setShowPopover(false);
    if (inputRef.current) {
      inputRef.current.value = ""; // Reset the input field's display
    }
  };

  return (
    <div className={`relative flex ${className}`} ref={containerRef}>
      <div
        onClick={() => setShowPopover(true)}
        className={`flex z-10 flex-1 relative [ nc-hero-field-padding ] flex-shrink-0 items-center space-x-3 cursor-pointer focus:outline-none text-left  ${
          showPopover ? "nc-hero-field-focused !rounded-md" : ""
        }`}
      >
        <div className="flex-grow">
          <p className="text-text-color font-semibold">{placeHolder}</p>
          <div className="relative w-full flex items-center">
            <input
              className={`block w-full pr-10 ${
                searchBar === false ? "text-text-color" : "text-text-color"
              } bg-transparent cursor-pointer border-none focus:ring-0 p-0 focus:outline-none ${
                searchBar === false
                  ? "focus:placeholder-text-color"
                  : "focus:placeholder-text-color"
              } xl:text-lg font-semibold ${
                searchBar === false ? "" : "placeholder-text-color"
              }  truncate `}
              placeholder={desc}
              value={
                searchedDropoffLocation.length > 0
                  ? searchedDropoffLocation
                  : dropOffLocation?.name
              }
              autoFocus={showPopover}
              onChange={(e) => {
                setSearchedDropoffLocation(e.currentTarget.value);
              }}
              ref={inputRef}
              readOnly
            />
            {(searchedDropoffLocation || dropOffLocation) &&
              ourLocation.filter((loc) => loc.active !== false).length > 1 && (
                <ClearDataButton onClick={handleClear} />
              )}
          </div>
        </div>
      </div>

      {showPopover && (
        <div
          className={`h-8 absolute self-center top-1/2 -translate-y-1/2 z-0  ${divHideVerticalLineClass}`}
        ></div>
      )}

      {showPopover &&
        ((searchedDropoffLocation && searchedLocations?.length > 0) ||
          ourLocation.filter((item) => item.active === true).length > 0) && (
          <div className="absolute overflow-y-auto left-0 z-40 w-full rounded-lg sm:min-w-[400px] top-full mt-3 max-h-96">
            {renderRecentSearches()}
          </div>
        )}
      <LocationDropoffModal
        isOpen={showLocationModal}
        selectedLocation={selectedLocation}
        onClose={() => setShowLocationModal(false)}
        ourCompany={ourCompany}
        setSearchedDropoffLocation={setSearchedDropoffLocation}
        setDropoffLocation={setDropoffLocation}
        ourLocation={ourLocation}
      />
    </div>
  );
};

export default DropOffLocationInput;
